import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { MaintenanceCalendarDashboardDto } from 'api/dashboard/dtos/MaintenanceCalendarDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import dayjs from 'dayjs';
import { FiltersFormModel } from '../filters/FiltersView';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import openMaintenancesList from '../utils/openMaintenancesList';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './MaintenanceCalendar.module.scss';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const MaintenanceCalendar = (props: Props) => {
	const { t, i18n } = useTranslation();
	const { addToast } = useToasts();
	const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
	const [events, setEvents] = useState<MaintenanceCalendarDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [tooltip, setTooltip] = useState<{ date: string; items: MaintenanceCalendarDashboardDto[]; x: number; y: number } | null>(null);
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom: currentMonth.toDate(),
					dateTo: currentMonth.endOf('month').toDate(),
				};
				const result = await DashboardService.getMaintenanceCalendar(criteria);
				if (!cancelled) setEvents(result || []);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, currentMonth]);

	const eventsByDay = useMemo(() => {
		const map: Record<number, MaintenanceCalendarDashboardDto[]> = {};
		events.forEach(evt => {
			const day = dayjs(evt.eventDate).date();
			if (!map[day]) map[day] = [];
			map[day].push(evt);
		});
		return map;
	}, [events]);

	const today = dayjs();
	const isCurrentMonth = currentMonth.isSame(today, 'month');
	const daysInMonth = currentMonth.daysInMonth();
	// Monday = 0, Sunday = 6. dayjs().day() returns 0=Sun, so convert
	const firstDayOfWeek = (currentMonth.day() + 6) % 7;

	const prevMonth = () => setCurrentMonth(m => m.subtract(1, 'month'));
	const nextMonth = () => setCurrentMonth(m => m.add(1, 'month'));

	const getTypeName = (item: MaintenanceCalendarDashboardDto) => {
		return item.typeIsSystem ? t(`common.maintenance_contract_type.${item.typeName}` as any) as string : item.typeName;
	};

	const handleDayMouseEnter = (day: number, evt: React.MouseEvent) => {
		const items = eventsByDay[day];
		if (!items || items.length === 0) return;
		setTooltip({
			date: currentMonth.date(day).format('DD/MM/YYYY'),
			items,
			x: evt.clientX,
			y: evt.clientY,
		});
	};

	const handleDayMouseMove = (evt: React.MouseEvent) => {
		setTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
	};

	const handleDayMouseLeave = () => {
		setTooltip(null);
	};

	const handleDayClick = (day: number) => {
		if (!eventsByDay[day]) return;
		const date = currentMonth.date(day);
		openMaintenancesList({
			dateFrom: date.startOf('day').toDate(),
			dateTo: date.endOf('day').toDate(),
			dateField: 'expected',
			currentState: 'PREDICTED',
			currentStateName: t('maintenances.states.PREDICTED') as string,
		});
	};

	const renderCalendarGrid = () => {
		const cells: React.ReactNode[] = [];

		// Day headers
		WEEKDAYS.forEach(day => {
			cells.push(
				<div key={`h-${day}`} className={styles.calDayHeader}>
					{t(`common.weekdays_short.${day}` as any)}
				</div>
			);
		});

		// Empty cells before first day
		for (let i = 0; i < firstDayOfWeek; i++) {
			cells.push(<div key={`e-${i}`} className={`${styles.calDay} ${styles.calDayOther}`} />);
		}

		// Day cells
		for (let d = 1; d <= daysInMonth; d++) {
			const isToday = isCurrentMonth && d === today.date();
			const hasEvents = !!eventsByDay[d];

			let className = styles.calDay;
			if (isToday && hasEvents) {
				className += ` ${styles.calDayTodayEvent}`;
			} else if (isToday) {
				className += ` ${styles.calDayToday}`;
			} else if (hasEvents) {
				className += ` ${styles.calDayEvent}`;
			}

			cells.push(
				<div
					key={`d-${d}`}
					className={className}
					onMouseEnter={hasEvents ? e => handleDayMouseEnter(d, e) : undefined}
					onMouseMove={hasEvents ? handleDayMouseMove : undefined}
					onMouseLeave={hasEvents ? handleDayMouseLeave : undefined}
					onClick={hasEvents ? () => handleDayClick(d) : undefined}
				>
					{d}
				</div>
			);
		}

		return cells;
	};

	const renderNavOptions = () => (
		<div className={styles.calNav}>
			<button className={styles.calNavBtn} onClick={prevMonth}>‹</button>
			<button className={styles.calNavBtn} onClick={nextMonth}>›</button>
		</div>
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.maintenance_calendar') as string}
			subTitleTooltip={t('dashboard.maintenance_calendar_tooltip' as any) as string}
			renderOptions={renderNavOptions()}
		>
			{isLoading ? (
				<div className={styles.centerAlign}><LoadingSpinner /></div>
			) : (
				<div className={styles.calendarContainer}>
					<div className={styles.calHeader}>
						<div className={styles.calTitle}>{currentMonth.locale(i18n.language).format('MMMM YYYY')}</div>
					</div>
					<div className={styles.calGrid}>
						{renderCalendarGrid()}
					</div>
				</div>
			)}
			{tooltip && (
				<ChartTooltipPortal clientX={tooltip.x} clientY={tooltip.y} className={styles.tooltip}>
					<div className={styles.tooltipDate}>{tooltip.date}</div>
					{tooltip.items.map((item, i) => (
						<div key={i} className={styles.tooltipItem}>
							<span className={styles.tooltipDot} />
							<span>
								<span className={styles.tooltipPlate}>{item.registrationNumber}</span>
								{' — '}
								{getTypeName(item)}
								{item.description ? ` · ${item.description}` : ''}
							</span>
						</div>
					))}
				</ChartTooltipPortal>
			)}
		</Box>
	);
};

export default MaintenanceCalendar;
